import { put, call, all, takeEvery, select } from "redux-saga/effects";
import _ from "lodash";
import { VIEWER, ENDPOINT } from "../constants";
import LocalStorageHelper from "../utils/helper/localstorage_helper";
import { setPageTopLoader, setViewerImageData } from "../actions";
import { getDataFromKpiDash } from "../api";
import AngleConfig from "../config/AngleConfig";
import { SAITAMA_CITY_PROVIDER } from "../config/SaitamaCityConfig";
import { buildSaitamaPhotoScope } from "../utils/helper/SaitamaCityPhotoQuery";

const S3_BUCKET_BASE_URL = process.env.REACT_APP_S3_BUCKET_BASE_URL;

export const startDate = (state) => state.app.dateRange[0];
export const endDate = (state) => state.app.dateRange[1];
export const periodType = (state) => state.app.periodType;
export const selectedMissionLabel = (state) => state.app.selectedMissionLabel;

const keywords = [
  "ぬまづの宝",
  "防災",
  "観光",
  "聖地巡礼",
  "ピクトレ",
  "当別町",
  "苫前町",
  "体験ミッション",
  "にいがた",
  "国際通り",
  "島めぐり",
  "那覇市",
  "SHIBUYA GAMES",
];

const buildViewerFileNameCandidates = (rawFileName = "") => {
  const decoded = String(rawFileName || "").trim();
  if (!decoded) return [];

  if (/\.(jpg|webp)$/i.test(decoded)) {
    return [decoded];
  }

  return [`${decoded}.jpg`, `${decoded}.webp`];
};

export const buildSaitamaViewerQuery = (groupKey) => {
  const decodedGroupKey = decodeURIComponent(String(groupKey || ""));
  const eventMatch = decodedGroupKey.match(/^event:([A-Za-z0-9]+)$/);
  const photoMatch = decodedGroupKey.match(/^photo:([A-Za-z0-9]+)$/);
  const fileMatch = decodedGroupKey.match(
    /^file:([^:]+):([^:]+):([^:]+):(\d{8}_\d{4})$/
  );
  const legacyFileName = String(groupKey || "").replace(/'/g, "''");

  const groupWhere = eventMatch
    ? `EXISTS (
        SELECT 1
        FROM power_asset_event group_pae
        WHERE group_pae.id = '${eventMatch[1]}'
          AND group_pae.type = 'PHOTOGRAPH'
          AND group_pae.game_space_id = ppa.game_space_id
          AND group_pae.power_asset_id = ppa.power_asset_id
          AND group_pae.player_id = ppa.player_id
      )`
    : fileMatch
    ? `ppa.game_space_id = '${fileMatch[1].replace(/'/g, "''")}'
       AND ppa.power_asset_id = '${fileMatch[2].replace(/'/g, "''")}'
       AND ppa.player_id = '${fileMatch[3].replace(/'/g, "''")}'
       AND SUBSTRING_INDEX(SUBSTRING_INDEX(ppa.file_name, '_', 4), '_', -2) = '${fileMatch[4]}'`
    : photoMatch
    ? `ppa.id = '${photoMatch[1]}'`
    : `ppa.file_name = '${legacyFileName}'`;
  return {
    query: `
      SELECT
        ppa.*,
        gs.*,
        gt.*,
        ps.*,
        pa.*,
        padsc.*,
        padsc.power_asset_id AS saitama_management_id,
        COALESCE(papaovi.angle_of_view_name, '') AS angle_of_view_name,
        CONVERT_TZ(pae.occurred_datetime, '+00:00', '+09:00') AS occurred_datetime_jst
      FROM photo_of_power_asset ppa
      JOIN game_space gs ON gs.id = ppa.game_space_id
      JOIN game_term gt ON gt.id = gs.game_term_id
      LEFT JOIN (
        SELECT player_id, MAX(name) AS name
        FROM player_state
        GROUP BY player_id
      ) ps ON ps.player_id = ppa.player_id
      JOIN power_asset pa ON pa.id = ppa.power_asset_id
      LEFT JOIN power_asset_detail_of_saitama_city padsc
        ON padsc.power_asset_id = ppa.power_asset_id
      LEFT JOIN inspection_photography_angle_of_view_info papaovi
        ON papaovi.inspection_photography_angle_of_view_id = ppa.inspection_photography_angle_of_view_id
        AND papaovi.language_code = 'JA'
      LEFT JOIN (
        SELECT game_space_id, power_asset_id, player_id, MAX(occurred_datetime) AS occurred_datetime
        FROM power_asset_event
        WHERE type = 'PHOTOGRAPH'
        GROUP BY game_space_id, power_asset_id, player_id
      ) pae
        ON pae.game_space_id = ppa.game_space_id
        AND pae.power_asset_id = ppa.power_asset_id
        AND pae.player_id = ppa.player_id
      WHERE ${groupWhere}
        AND ${buildSaitamaPhotoScope("ppa")}
      ORDER BY
        CASE
          WHEN ppa.inspection_photography_angle_of_view_id REGEXP '^[0-9]+$'
            THEN CAST(ppa.inspection_photography_angle_of_view_id AS UNSIGNED)
          ELSE 999999
        END,
        ppa.id DESC;
    `,
  };
};

export function* handleImageDataByFileName(actions) {
  try {
    yield put(setPageTopLoader(true));
    const companyName = LocalStorageHelper.getCompany();
    const isSaitama = companyName === SAITAMA_CITY_PROVIDER.company;
    const requestedGroupKey = String(actions.payload || "");
    const isSaitamaGroupKey =
      isSaitama && /^(event|file|photo):/.test(requestedGroupKey);
    const currentPeriodType = yield select(periodType);
    const missionSelectedName = yield select(selectedMissionLabel);
    const isSpecialMission =
      !!missionSelectedName &&
      keywords.some((keyword) => missionSelectedName.includes(keyword));
    const usePadtepJoin =
      !(companyName === "hokuden" && currentPeriodType === "mission") &&
      !isSpecialMission;

    const fileNameCandidates = isSaitamaGroupKey
      ? []
      : buildViewerFileNameCandidates(actions.payload).map((name) =>
          name.replace("本柱 支柱", "本柱＋支柱")
        );
    const safeFileNameCandidates = fileNameCandidates.map((name) => name.replace(/'/g, "''"));
    const requestedFileName = fileNameCandidates[0] || String(actions.payload || "");
    if (!isSaitamaGroupKey && !safeFileNameCandidates.length) {
      yield put(setViewerImageData([]));
      yield put(setPageTopLoader(false));
      return;
    }

    const env = process.env.REACT_APP_ENV;
    let companyTableName = `power_asset_detail_of_tepco`;
    if (companyName === "ntt") {
      companyTableName = `power_asset_detail_of_ntt_me`;
    } else if (companyName === "ntt-ft") {
      companyTableName = env === "dev" ? `power_asset_detail_of_ntt_me` : `power_asset_detail_of_ntt_ft`;
    } else if (companyName === "okiden") {
      companyTableName = `power_asset_detail_of_ntt_ft`;
    } else if (companyName === "hokuden") {
      companyTableName = `power_asset_detail_of_hepco`;
    }

    const joinTableAdjustment = companyTableName;

    //URLからseason不明を判明
    const sqlGetPpa = {
      query: `
        SELECT
          ppa.id AS ppa_id,
          ppa.power_asset_id,
          ppa.player_id,
          ppa.game_space_id,
          ppa.file_name,
          ppa.object_key
        FROM photo_of_power_asset ppa
        WHERE ppa.file_name IN (${safeFileNameCandidates.map((name) => `'${name}'`).join(", ")})
          ${isSaitama ? `AND ${buildSaitamaPhotoScope("ppa")}` : ""}
        ORDER BY FIELD(ppa.file_name, ${safeFileNameCandidates.map((name) => `'${name}'`).join(", ")}) ASC,
                 ppa.id DESC
        LIMIT 1;
      `,
    };

    const ppaRow = isSaitamaGroupKey
      ? null
      : JSON.parse(
          (yield call(
            getDataFromKpiDash,
            sqlGetPpa,
            ENDPOINT.GET_DATA_API_REPLICA
          )).body
        )?.[0];
    if (!ppaRow && !isSaitamaGroupKey) {
      yield put(setViewerImageData([]));
      yield put(setPageTopLoader(false));
      return;
    }
    const resolvedFileName = ppaRow?.file_name?.replace(/'/g, "''");

    if (isSaitama) {
      const [saitamaViewerTemp] = yield all([
        call(
          getDataFromKpiDash,
          buildSaitamaViewerQuery(
            isSaitamaGroupKey ? requestedGroupKey : resolvedFileName
          ),
          ENDPOINT.GET_DATA_API_REPLICA
        ),
      ]);
      const saitamaViewerData = JSON.parse(saitamaViewerTemp.body || "[]");
      if (!saitamaViewerData.length) {
        yield put(setViewerImageData([]));
        yield put(setPageTopLoader(false));
        return;
      }
      saitamaViewerData.forEach((photoData) => {
        photoData.signedUrl = `${S3_BUCKET_BASE_URL}/${encodeURIComponent(photoData.object_key)}`;
        photoData.review_request_id = null;
      });
      const angleNameOrder = new Map(
        (AngleConfig.angleNameSort || []).map((name, index) => [name, index])
      );
      const sortedSaitamaViewerData = _.sortBy(saitamaViewerData, [
        (photoData) => {
          const orderIndex = angleNameOrder.get(photoData?.angle_of_view_name);
          return Number.isFinite(orderIndex) ? orderIndex : Number.POSITIVE_INFINITY;
        },
        (_photoData, index) => index,
      ]);
      yield put(setViewerImageData(sortedSaitamaViewerData));
      yield put(setPageTopLoader(false));
      return;
    }

    // review_request_photo_bind ではなく review_request から review_request_id を引く
    const sqlGetReviewRequest = {
      query: `
        SELECT rr.id AS review_request_id
        FROM review_request rr
        JOIN review_request_event rre
          ON rre.review_request_id = rr.id
        WHERE rr.power_asset_id = '${ppaRow.power_asset_id}'
          AND rr.player_id = '${ppaRow.player_id}'
          AND rr.game_space_id = '${ppaRow.game_space_id}'
          AND rre.type = 'APPLIED'
        ORDER BY rre.occurred_datetime DESC
        LIMIT 1;
      `,
    };

    let reviewRequestId;
    try {
      const [reviewRequestTemp] = yield all([
        call(getDataFromKpiDash, sqlGetReviewRequest, ENDPOINT.GET_DATA_API_REPLICA),
      ]);
      const reviewRequestRow = JSON.parse(reviewRequestTemp.body)?.[0];
      reviewRequestId = reviewRequestRow?.review_request_id;
    } catch (_error) {
      reviewRequestId = undefined;
    }

    // Query step1: Get review_request_id by a param file name
    // ---------- Query step1: review_request_id 取得 ----------
    // ★ 特殊ミッションの場合は padtep を SELECT / JOIN から除外
    const padtepSelectFragment = usePadtepJoin ? ", padtep.*" : "";
    const padtepJoinFragment = usePadtepJoin
      ? `
        JOIN ${joinTableAdjustment} padtep
          ON pae.power_asset_id = padtep.power_asset_id
      `
      : "";

    const reviewRequestIdSelectValue = reviewRequestId
      ? `'${reviewRequestId}'`
      : "NULL";
    const sqlQuery1 = {
      query: `
        SELECT
            ppa.*,
            gs.*,
            gt.*,
            pae.occurred_datetime AS occurred_datetime,
            CONVERT_TZ(pae.occurred_datetime, '+00:00', '+09:00') AS occurred_datetime_jst,
            ps.*${padtepSelectFragment},
            ${reviewRequestIdSelectValue} AS review_request_id,
            pa.*
        FROM photo_of_power_asset ppa
        JOIN power_asset_event pae
          ON pae.power_asset_id = ppa.power_asset_id
        AND pae.player_id      = ppa.player_id
        AND pae.game_space_id  = ppa.game_space_id
        JOIN game_space gs ON gs.id = ppa.game_space_id
        JOIN game_term gt ON gt.id = gs.game_term_id
        JOIN player_state ps ON ps.player_id = ppa.player_id
        ${padtepJoinFragment}
        JOIN power_asset pa ON pa.id = ppa.power_asset_id
        WHERE
          pae.type = 'PHOTOGRAPH'
          AND ppa.file_name = '${resolvedFileName}'
        ORDER BY pae.occurred_datetime DESC
        LIMIT 1
      `,
    };

    const [imageReviewDataTemp] = yield all([
      call(getDataFromKpiDash, sqlQuery1, ENDPOINT.GET_DATA_API_REPLICA),
    ]);
    const imageReviewData = JSON.parse(imageReviewDataTemp.body) || [];
    if (!imageReviewData.length) {
      console.log("No imageReviewData for fileName:", requestedFileName);
      yield put(setViewerImageData([]));
      yield put(setPageTopLoader(false));
      return;
    }

    // ---------- Query step2: 新構成の review_request_info 系から画像群を取得 ----------
    const buildSqlQuery2 = (withReviewRequestFilter) => {
      const reviewRequestFilterQuery =
        withReviewRequestFilter && reviewRequestId
          ? `AND rrrrib.review_request_id = '${reviewRequestId}'`
          : ``;
      return {
        query: `
          WITH target AS (
            SELECT
              ppa.power_asset_id,
              ppa.player_id,
              ppa.game_space_id,
              rrrrib.review_request_id,
              rre.occurred_datetime
            FROM photo_of_power_asset ppa
            JOIN review_image_info rii ON rii.photo_of_power_asset_id = ppa.id
            JOIN review_request_info_review_image_bind rririb ON rririb.review_image_info_id = rii.id
            JOIN review_request_info rri ON rri.id = rririb.review_request_info_id
            JOIN review_request_review_request_info_bind rrrrib ON rrrrib.review_request_info_id = rri.id
            JOIN review_request rr ON rr.id = rrrrib.review_request_id
            JOIN review_request_event rre
              ON rre.review_request_id = rr.id
            AND rre.type = 'APPLIED'
            WHERE ppa.file_name = '${resolvedFileName}'
            ${reviewRequestFilterQuery}
            ORDER BY rre.occurred_datetime DESC
            LIMIT 1
          )
          SELECT
            ppa.*,
            gs.*,
            gt.*,
            ps.*,

            ${usePadtepJoin ? "padtep.*," : ""}

            DATE_FORMAT(
              CONVERT_TZ(t.occurred_datetime, '+00:00', '+09:00'),
              '%Y-%m-%d %H:%i:%s'
            ) AS occurred_datetime_jst,
            COALESCE(papaovi.angle_of_view_name, '') AS angle_of_view_name,
            t.review_request_id AS resolved_review_request_id,
            paiwais.power_asset_id AS insulated_switch,

            pa.latitude,
            pa.longitude,
            pa.sub_type,
            pa.id AS pa_id

          FROM target t
          JOIN photo_of_power_asset ppa
            ON ppa.power_asset_id = t.power_asset_id
          AND ppa.player_id      = t.player_id
          AND ppa.game_space_id  = t.game_space_id

          JOIN review_request_review_request_info_bind rrrrib
            ON rrrrib.review_request_id = t.review_request_id
          JOIN review_request_info rri ON rri.id = rrrrib.review_request_info_id
          JOIN review_request_info_review_image_bind rririb
            ON rririb.review_request_info_id = rri.id
          JOIN review_image_info rii
            ON rii.photo_of_power_asset_id = ppa.id
          AND rririb.review_image_info_id = rii.id

          JOIN game_space gs ON gs.id = ppa.game_space_id
          JOIN game_term gt ON gt.id = gs.game_term_id
          JOIN player_state ps ON ps.player_id = ppa.player_id
          JOIN power_asset pa ON pa.id = ppa.power_asset_id
          LEFT JOIN inspection_photography_angle_of_view_info papaovi
            ON papaovi.inspection_photography_angle_of_view_id = ppa.inspection_photography_angle_of_view_id
            AND papaovi.language_code = 'JA'
          LEFT JOIN power_asset_id_with_air_insulated_switch paiwais
            ON paiwais.power_asset_id = ppa.power_asset_id
          ${usePadtepJoin ? `LEFT JOIN ${joinTableAdjustment} padtep ON padtep.power_asset_id = ppa.power_asset_id` : ""}

          ORDER BY ppa.file_name ASC;
        `,
      };
    };

    let imageFilesData = [];
    const fetchImageFilesData = function* (withReviewRequestFilter) {
      const sqlQuery2 = buildSqlQuery2(withReviewRequestFilter);
      const [imageFilesDataTemp] = yield all([
        call(getDataFromKpiDash, sqlQuery2, ENDPOINT.GET_DATA_API_REPLICA),
      ]);
      return JSON.parse(imageFilesDataTemp.body || "[]");
    };

    try {
      imageFilesData = yield* fetchImageFilesData(true);
    } catch (_error) {
      imageFilesData = [];
    }

    if (!imageFilesData.length && reviewRequestId) {
      console.warn(
        "[viewerSaga] review_request_id filter returned no data, retrying without filter",
        {
          companyName,
          requestedFileName,
          reviewRequestId,
        }
      );
      try {
        imageFilesData = yield* fetchImageFilesData(false);
      } catch (_error) {
        imageFilesData = [];
      }
    }

    // Query step3: Get all signedUrls
    let photoPathArray = [],
      photoFileNameArray = [];
    _.each(imageFilesData, function (photoData) {
      photoPathArray.push(photoData.object_key);
      photoFileNameArray.push(photoData.file_name);
    });

    _.each(imageFilesData, function (photoData, index) {
      photoData.signedUrl = `${S3_BUCKET_BASE_URL}/${encodeURIComponent(
        photoPathArray[index]
      )}`;
      photoData.review_request_id =
        photoData.resolved_review_request_id || reviewRequestId || null;
    });

    if (!imageFilesData?.length) {
      yield put(setViewerImageData([]));
      yield put(setPageTopLoader(false));
      return;
    }

    // update 0 index data from review request dateTime to photo sent dateTime
    imageFilesData[0].occurred_datetime_jst = imageReviewData[0][
      "occurred_datetime_jst"
    ]
      .slice(0, 19)
      .replace("T", " ");
    const angleNameOrder = new Map(
      (AngleConfig.angleNameSort || []).map((name, index) => [name, index])
    );
    imageFilesData = _.sortBy(
      imageFilesData,
      [
        (photoData) => {
          const orderIndex = angleNameOrder.get(photoData?.angle_of_view_name);
          return Number.isFinite(orderIndex) ? orderIndex : Number.POSITIVE_INFINITY;
        },
        (_photoData, index) => index,
      ]
    );

    yield put(setViewerImageData(imageFilesData));
    yield put(setPageTopLoader(false));
  } catch (error) {
    console.log(error);
    yield put(setPageTopLoader(false));
  }
}

export default function* watchMapSettings() {
  yield takeEvery(VIEWER.GET_IMAGE_DATA_BY_FILENAME, handleImageDataByFileName);
}
